import React, { useRef, useState, useEffect } from 'react';
import styles from './DACIContacts.module.scss';
import AddIcon from '@spectrum-icons/workflow/Add';
import Edit from '@spectrum-icons/workflow/Edit';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import DACIContactPopup, {
  Contact as DACIContact
} from '../../DACIContactPopup';
import Avatar from './Avatar';
import { useRBAC, EditOnlyWrapper } from '../context/RBACContext';
import {
  resolveContactEmail,
  type ContactEmailSource
} from '../../../services/contactEmailUtils';
import { DACI_ROLES, DRIVER_ROLES } from '../../../constants/daciRoles';

// Use the Contact interface from DACIContactPopup
type Contact = DACIContact;

interface RoleSection {
  title: string;
  contacts: Contact[];
}

interface DACIContactsProps {
  daciData?: {
    contacts: Contact[];
    roles: {
      Driver: { title: string; description: string; displayOrder: number };
      Approver: { title: string; description: string; displayOrder: number };
      Contributor: { title: string; description: string; displayOrder: number };
      Informed: { title: string; description: string; displayOrder: number };
    };
  };
  sections?: RoleSection[]; // Legacy support
  isExpanded?: boolean; // Control visibility of DACI contacts list
  onAddContact?: (contact: Contact) => void;
  onEditContact?: (contact: Contact, originalContact: Contact | null) => void;
  onDeleteContact?: (contact: Contact) => void;
}

export function DACIContacts({
  daciData,
  sections,
  isExpanded = true,
  onAddContact,
  onEditContact,
  onDeleteContact
}: DACIContactsProps): React.ReactElement {
  const tableRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [preSelectedRole, setPreSelectedRole] = useState<string | undefined>(
    undefined
  );

  // Transform new flat structure into sections for rendering
  const getSectionsFromDaciData = (): RoleSection[] => {
    if (!daciData?.contacts || !daciData?.roles) return [];

    const roleOrder = ['Driver', 'Approver', 'Contributor', 'Informed'];

    return roleOrder
      .map(roleName => {
        const roleContacts = daciData.contacts.filter(
          contact =>
            contact.daciRole === roleName &&
            contact.name &&
            contact.name.trim() !== ''
        );
        return {
          title: roleName,
          contacts: roleContacts
        };
      })
      .filter(section => section.contacts.length > 0);
  };

  // Use either new daciData structure or legacy sections
  const displaySections = daciData ? getSectionsFromDaciData() : sections || [];

  const checkScroll = () => {
    if (tableRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = tableRef.current;
      setShowScrollIndicator(
        scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth
      );
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const handleScroll = () => {
    checkScroll();
  };

  const scrollRight = () => {
    if (tableRef.current) {
      tableRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleOpenPopup = (roleName?: string) => {
    setIsPopupOpen(true);
    setIsEditMode(false);
    setEditingContact(null);
    setPreSelectedRole(roleName);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setIsEditMode(false);
    setEditingContact(null);
    setPreSelectedRole(undefined);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setIsEditMode(true);
    setIsPopupOpen(true);
  };

  const handleSaveContact = (contactData: Contact) => {
    const source: ContactEmailSource = contactData;
    const formattedContact = {
      daciRole: contactData.daciRole || contactData.daciTitle || 'Driver',
      role: contactData.role,
      name: contactData.name,
      uid: contactData.uid,
      email: resolveContactEmail(source),
      imageUrl: contactData.imageUrl || 'https://via.placeholder.com/32'
    };

    if (isEditMode && editingContact && onEditContact) {
      // In edit mode, check if this contact is the original one being edited
      // or a new contact being added
      const isOriginalContact =
        editingContact.uid === contactData.uid &&
        editingContact.name === contactData.name;

      if (isOriginalContact) {
        // This is the original contact being edited
        onEditContact(formattedContact, editingContact);
      } else {
        // This is a new contact being added alongside the edit
        if (onAddContact) {
          onAddContact(formattedContact);
        }
      }
    } else if (onAddContact) {
      // Add mode
      onAddContact(formattedContact);
    }

    handleClosePopup();
  };

  // Get all existing contacts from all sections for the popup
  const getAllExistingContacts = () => {
    return displaySections.flatMap(section =>
      section.contacts.map(contact => ({
        ...contact,
        uid: contact.uid || contact.name.toLowerCase().replace(/\s+/g, '.') // Generate uid if not present
      }))
    );
  };

  const daciRoles = DACI_ROLES;
  const driverRoles = DRIVER_ROLES;

  const contacts = [
    { id: 'contact1', name: 'John Doe' },
    { id: 'contact2', name: 'Jane Smith' },
    { id: 'contact3', name: 'Mike Johnson' },
    { id: 'contact4', name: 'Sarah Wilson' },
    { id: 'contact5', name: 'Alex Brown' }
  ];

  return (
    <div className={styles.daciContainer} data-testid="daci-contacts-container">
      {/* Only show DACI contacts list when expanded */}
      {isExpanded && (
        <div
          ref={tableRef}
          className={styles.daciTable}
          onScroll={handleScroll}
          data-testid="daci-list"
        >
          {displaySections.length === 0 ? (
            <div className={styles.emptyState} data-testid="daci-empty-state">
              <p className={styles.emptyMessage}>
                No DACI contacts have been added yet.
              </p>
              <EditOnlyWrapper>
                <button
                  className={styles.addFirstContactButton}
                  onClick={() => handleOpenPopup()}
                  data-testid="daci-add-first-button"
                >
                  Add Contact
                </button>
              </EditOnlyWrapper>
            </div>
          ) : (
            displaySections.map((section, index) => (
              <div key={index} className={styles.roleSection}>
                <div className={styles.roleHeader}>
                  <h3 className={styles.roleTitle}>{section.title}</h3>
                  <EditOnlyWrapper>
                    <button
                      className={styles.roleAddButton}
                      onClick={() => handleOpenPopup(section.title)}
                      aria-label={`Add ${section.title}`}
                      data-testid={`daci-add-${section.title.toLowerCase()}-button`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8 3V13M3 8H13"
                          stroke="#909090"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </EditOnlyWrapper>
                </div>
                <div className={styles.contactsList}>
                  {section.contacts.map((contact, contactIndex) => (
                    <div key={contactIndex} className={styles.contactItem}>
                      {/* Only show role for Driver section */}
                      {contact.role && section.title === 'Driver' && (
                        <div className={styles.role}>{contact.role}</div>
                      )}
                      <div className={styles.contactInfo}>
                        <div className={styles.profilePicture}>
                          <Avatar
                            src={
                              contact.imageUrl ||
                              'https://via.placeholder.com/32'
                            }
                            alt={contact.name}
                            name={contact.name}
                            size={32}
                          />
                        </div>
                        <div className={styles.contactDetails}>
                          <div className={styles.nameContainer}>
                            <span className={styles.contactName}>
                              {contact.name}
                            </span>
                            <EditOnlyWrapper>
                              <button
                                className={styles.editButton}
                                onClick={() => handleEditContact(contact)}
                                aria-label={`Edit ${contact.name}`}
                              >
                                <Edit size="S" />
                              </button>
                            </EditOnlyWrapper>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {isExpanded && showScrollIndicator && (
        <button
          className={styles.scrollIndicator}
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <ChevronRight />
        </button>
      )}

      {/* DACI Contact Popup */}
      <DACIContactPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onSave={handleSaveContact}
        onDelete={onDeleteContact}
        daciRoles={daciRoles}
        driverRoles={driverRoles}
        contacts={contacts}
        existingContacts={getAllExistingContacts()}
        initialContact={editingContact || undefined}
        preSelectedRole={preSelectedRole}
      />
    </div>
  );
}
